import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { IconName } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Link } from '@delmonicos/shared-design-system-react/src/components/link/link';
import { GapEnum } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextWeight,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { TicketRelatedResourceType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/enums/ticket-related-resource-type.enum';
import { RelatedResourceModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/outputs/related-resource.model';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { useCpmsBoGetChargePointQuery } from '@service/cpms-bo-charge-point.service';
import { useCpmsBoGetLocationQuery } from '@service/cpms-bo-location.service';

interface RelatedResourceComponentProps {
  resource: RelatedResourceModel;
}

const RESOURCE_ICON: Record<TicketRelatedResourceType, IconName> = {
  [TicketRelatedResourceType.ChargePoint]: 'terminal',
  [TicketRelatedResourceType.Location]: 'marker-pin-01',
  [TicketRelatedResourceType.Channel]: 'wire',
  [TicketRelatedResourceType.PhysicalSocket]: 'electric-terminal',
};

const RESOURCE_LABEL_KEY: Record<TicketRelatedResourceType, string> = {
  [TicketRelatedResourceType.ChargePoint]:
    '$.ticket-related-resources.type-charge-point-id',
  [TicketRelatedResourceType.Location]:
    '$.ticket-related-resources.type-location-id',
  [TicketRelatedResourceType.Channel]:
    '$.ticket-related-resources.type-channel',
  [TicketRelatedResourceType.PhysicalSocket]:
    '$.ticket-related-resources.type-physical-socket',
};

const RESOURCE_PATH_RESOLVER: Partial<
  Record<TicketRelatedResourceType, (id: string) => string>
> = {
  [TicketRelatedResourceType.ChargePoint]: (id) =>
    generatePath(APP_PATH.CHARGE_POINT.DETAILS, { id }),
  [TicketRelatedResourceType.Location]: (id) =>
    generatePath(APP_PATH.LOCATION.DETAILS, { id }),
};

export const RelatedResourceComponent = ({
  resource,
}: RelatedResourceComponentProps): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isChargePoint = resource.type === TicketRelatedResourceType.ChargePoint;
  const isLocation = resource.type === TicketRelatedResourceType.Location;

  const { data: chargePoint } = useCpmsBoGetChargePointQuery(resource.id, {
    skip: !isChargePoint,
  });
  const { data: location } = useCpmsBoGetLocationQuery(resource.id, {
    skip: !isLocation,
  });

  const name = isChargePoint
    ? chargePoint?.name
    : isLocation
      ? location?.name
      : undefined;
  const path = RESOURCE_PATH_RESOLVER[resource.type]?.(resource.id);
  const label = name ?? resource.id;

  return (
    <Card gap={GapEnum.M}>
      <AccentTitle
        variant={AccentVariantEnum.Secondary}
        icon={RESOURCE_ICON[resource.type]}
        text={t(RESOURCE_LABEL_KEY[resource.type])}
      />
      {path ? (
        <Link onClick={() => navigate(path)}>
          <Text fontWeight={TextWeight.SemiBold}>{label}</Text>
        </Link>
      ) : (
        <Text fontWeight={TextWeight.SemiBold}>{label}</Text>
      )}
    </Card>
  );
};
